import { useState, type FormEvent } from "react";
import { X } from "lucide-react";
import { Modal } from "@veorest/ui";
import { useI18n } from "../../i18n";

/** Minimal client fields needed for editing (works for list rows and report rows). */
export interface ClientEditableFields {
  id: string;
  name: string;
  phone: string | null;
  address: string | null;
  username: string | null;
  owner_email: string | null;
}

export interface ClientEditValues {
  name: string;
  phone: string;
  address: string;
  username: string;
  email: string;
}

interface ClientEditModalProps {
  open: boolean;
  busy?: boolean;
  client: ClientEditableFields | null;
  /** True once the store is activated — the login username is then permanent. */
  usernameLocked?: boolean;
  onClose: () => void;
  onSubmit: (values: ClientEditValues) => Promise<void>;
}

export function ClientEditModal({
  open,
  busy,
  client,
  usernameLocked = false,
  onClose,
  onSubmit,
}: ClientEditModalProps) {
  const { t } = useI18n();
  const [name, setName] = useState(client?.name ?? "");
  const [phone, setPhone] = useState(client?.phone ?? "");
  const [address, setAddress] = useState(client?.address ?? "");
  const [username, setUsername] = useState(client?.username ?? "");
  const [email, setEmail] = useState(client?.owner_email ?? "");

  if (!open || !client) return null;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    await onSubmit({
      name: name.trim(),
      phone: phone.trim(),
      address: address.trim(),
      username: username.trim().toLowerCase(),
      email: email.trim(),
    });
  };

  return (
    <Modal open={open} onClose={onClose} panelClassName="max-w-md">
      <div className="flex max-h-[calc(100vh-32px)] flex-col overflow-y-auto">
        <div className="flex items-center justify-between border-b border-[var(--border-subtle)] px-5 py-4">
          <h2
            id="edit-client-title"
            className="text-lg font-extrabold text-[var(--text-primary)]"
          >
            {t("clients.editTitle")}
          </h2>
          <button
            type="button"
            className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-[var(--border-subtle)]"
            onClick={onClose}
            aria-label={t("common.close")}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4 px-5 py-4">
          <p className="text-xs text-[var(--text-muted)]">{t("clients.editHint")}</p>

          <label className="block space-y-1.5">
            <span className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">
              {t("clients.businessName")}
            </span>
            <input
              className="input-base w-full"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              minLength={2}
            />
          </label>

          <label className="block space-y-1.5">
            <span className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">
              {t("clients.phone")}
            </span>
            <input
              className="input-base w-full"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              inputMode="tel"
            />
          </label>

          <label className="block space-y-1.5">
            <span className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">
              {t("clients.address")}
            </span>
            <input
              className="input-base w-full"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              required
            />
          </label>

          <label className="block space-y-1.5">
            <span className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">
              {t("clients.username")}
            </span>
            <input
              className="input-base w-full disabled:opacity-60"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              minLength={3}
              maxLength={30}
              autoCapitalize="none"
              spellCheck={false}
              readOnly={usernameLocked}
              disabled={usernameLocked}
            />
            <span className="text-[11px] text-[var(--text-muted)]">
              {usernameLocked ? t("accounts.usernameLocked") : t("clients.usernameHint")}
            </span>
          </label>

          <label className="block space-y-1.5">
            <span className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">
              {t("clients.emailOptional")}
            </span>
            <input
              className="input-base w-full"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              className="btn-secondary flex-1"
              onClick={onClose}
              disabled={busy}
            >
              {t("common.cancel")}
            </button>
            <button type="submit" className="btn-primary flex-1" disabled={busy}>
              {busy ? t("clients.creating") : t("clients.save")}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
